import * as React from 'react';

import type { SideNavigationProps } from '@amzn/awsui-components-console';
import { SideNavigation } from '@amzn/awsui-components-console';
import { useHistory } from 'react-router-dom';
import { BREADCRUMBS } from '../pages/pages';
import { getSites } from '../utils/accountsUtils';
import { useAppSettings } from '../context/AppSettings';
import { useDoesUserHavePermissionClaim } from '../context/Permissions';
import { PermissionClaim } from '../code-generated/PermissionClaim.generated';

export function NavigationSidebar() {
  const history = useHistory();
  const { stage } = useAppSettings();
  const canReadSiteHealthAndStatus = useDoesUserHavePermissionClaim(PermissionClaim.ReadSiteHealthAndStatus);
  const canValidateCustomerOnboarding = useDoesUserHavePermissionClaim(PermissionClaim.CustomerOnboardingValidation);

  return (
    <SideNavigation
      items={NavigationItems(stage, canReadSiteHealthAndStatus === true, canValidateCustomerOnboarding === true)}
      onFollow={React.useCallback(
        (ev: CustomEvent<SideNavigationProps.FollowDetail>) => {
          ev.preventDefault();
          if (ev.detail.href) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            history.push(ev.detail.href);
          }
        },
        [history],
      )}
      activeHref={history.location.pathname}
    />
  );
}

function NavigationItems(
  stage: string,
  canReadSiteHealthAndStatus: boolean,
  canValidateCustomerOnboarding: boolean,
): readonly SideNavigationProps.Item[] {
  const customerOnboardingItems: SideNavigationProps.Link[] = [];

  if (canValidateCustomerOnboarding) {
    customerOnboardingItems.push({
      type: 'link',
      ...BREADCRUMBS.CustomerOnboardingValidator,
    });
    customerOnboardingItems.push({
      type: 'link',
      ...BREADCRUMBS.ListSatellites,
    });
  }

  // Always show Satellite Onboarding — the page handles CAZ prompting when credentials aren't active.
  customerOnboardingItems.push({
    type: 'link',
    ...BREADCRUMBS.SatelliteOnboarding,
  });

  // Always show CPPA Onboarding — the page handles CAZ prompting when credentials aren't active.
  customerOnboardingItems.push({
    type: 'link',
    ...BREADCRUMBS.CppaOnboarding,
  });

  return [
    { type: 'link', ...BREADCRUMBS.Home },
    { type: 'link', ...BREADCRUMBS.Oncall },
    {
      type: 'link',
      ...BREADCRUMBS.Authorization,
    },
    {
      type: 'section',
      text: 'Contacts',
      items: [
        {
          type: 'link',
          ...BREADCRUMBS.ListContacts,
        },
        {
          type: 'link',
          ...BREADCRUMBS.ContactSearch,
        },
      ],
    },
    {
      type: 'section',
      text: 'Site Management',
      items: [
        {
          type: 'link',
          ...BREADCRUMBS.SiteTimelines,
        },
        {
          type: 'link',
          ...BREADCRUMBS.MaintenanceList,
        },
        {
          type: 'link',
          ...BREADCRUMBS.MaintenanceSchedule,
        },
        {
          type: 'link',
          ...BREADCRUMBS.SNAP,
        },
        {
          type: 'link',
          ...BREADCRUMBS.UplinkRestrictions,
        },
      ],
    },
    // TODO: gate this on the inventory permission claims (ReadInventory, WriteInventory or
    // ManageInventory, matching the API's OR semantics) once they exist in
    // AwsGsaasAtacamaPermissions and graphql-codegen picks them up.
    //
    // Deliberately NOT gated on ReadSiteHealthAndStatus. That population differs from the
    // inventory one, so proxying it would show this section to users who then hit a permission
    // empty state on every page inside it. Leaving it ungated until the real claims land is the
    // honest interim: the UI is an affordance layer, never the security boundary, and the API
    // enforces authorization on every read regardless of what the sidebar shows.
    { type: 'link', ...BREADCRUMBS.Inventory },
    ...(customerOnboardingItems.length > 0
      ? ([
          {
            type: 'section',
            text: 'Customer Onboarding',
            items: customerOnboardingItems,
          },
        ] as readonly SideNavigationProps.Item[])
      : []),
    {
      type: 'section',
      text: 'Customer Management',
      items: [
        {
          type: 'link',
          ...BREADCRUMBS.ViewCustomer,
        },
        {
          type: 'link',
          ...BREADCRUMBS.ViewMultipleCustomers,
        },
      ],
    },
    {
      type: 'section',
      text: 'Dashboards',
      items: [
        {
          type: 'link',
          ...BREADCRUMBS.DataPlaneDashboard,
        },
      ],
    },
    ...(canReadSiteHealthAndStatus
      ? ([
          {
            type: 'section',
            text: 'Site Status',
            items: getSites(stage).map(site => ({
              type: 'link',
              ...BREADCRUMBS.Site(site.groundStationId),
            })),
          },
        ] as readonly SideNavigationProps.Item[])
      : []),
    {
      type: 'section',
      text: 'Dev Tools',
      items: [
        {
          type: 'link',
          ...BREADCRUMBS.DataExplorer,
        },
      ],
    },
  ];
}
